import { useEffect, useState } from "react";
import { Briefcase, Check, Plus, Search } from "lucide-react";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";

/**
 * Multi-select Client picker used in TimerBar. `value` is an array of client
 * IDs. `onChange(next)` receives the next array. `onCreate({name, color})`
 * (optional) allows in-place creation.
 */
export default function ClientMultiPicker({ items, value, onChange, onCreate, testId }) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  useEffect(() => { if (open) setSearch(""); }, [open]);
  const selected = (value || []).map((id) => items.find((c) => c.id === id)).filter(Boolean);
  const query = search.trim().toLowerCase();
  const filtered = items.filter((c) => !query || c.name.toLowerCase().includes(query));
  const exact = items.find((c) => c.name.toLowerCase() === query);

  const toggle = (id) => {
    const next = (value || []).includes(id)
      ? (value || []).filter((x) => x !== id)
      : [...(value || []), id];
    onChange(next);
  };

  const createNew = async () => {
    if (!query || exact || !onCreate) return;
    const created = await onCreate({ name: search.trim(), color: "#145c8c" });
    if (created?.id) {
      onChange([...(value || []), created.id]);
      setSearch("");
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="h-8 px-2 rounded-md hover:bg-slate-100 text-slate-600 flex items-center gap-1 text-xs max-w-[280px]"
          data-testid={testId}
        >
          <Briefcase className="h-3.5 w-3.5 text-slate-400 shrink-0" />
          {selected.length === 0 ? (
            <span className="text-slate-400">Client</span>
          ) : (
            <span className="flex items-center gap-1 overflow-hidden">
              {selected.slice(0, 2).map((c) => (
                <span key={c.id}
                      className="px-1.5 py-0.5 rounded-full text-[10px] font-semibold whitespace-nowrap"
                      style={{ background: `${c.color}1a`, color: c.color }}
                      data-testid={`${testId}-pill-${c.id}`}>
                  {c.name}
                </span>
              ))}
              {selected.length > 2 && (
                <span className="text-[10px] text-slate-500">+{selected.length - 2}</span>
              )}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-2" align="end">
        <div className="flex items-center gap-1 mb-2">
          <Search className="h-3.5 w-3.5 text-slate-400" />
          <input value={search}
                 onChange={(e) => setSearch(e.target.value)}
                 placeholder="Search or create…"
                 className="flex-1 text-sm px-1 py-1 outline-none placeholder:text-slate-400"
                 data-testid={`${testId}-search`} />
        </div>
        <div className="max-h-48 overflow-y-auto">
          {filtered.length === 0 && !query ? (
            <div className="px-2 py-3 text-xs text-slate-400 text-center">No clients yet.</div>
          ) : filtered.map((c) => {
            const isOn = (value || []).includes(c.id);
            return (
              <button
                key={c.id}
                onClick={() => toggle(c.id)}
                className={`w-full text-left px-2 py-1.5 rounded hover:bg-slate-100 flex items-center gap-2 text-sm ${isOn ? "bg-slate-50" : ""}`}
                data-testid={`${testId}-option-${c.id}`}
              >
                <span className="h-2.5 w-2.5 rounded-full" style={{ background: c.color }} />
                <span className="flex-1 truncate">{c.name}</span>
                {isOn && <Check className="h-3.5 w-3.5 text-[#145c8c]" />}
              </button>
            );
          })}
        </div>
        {query && !exact && onCreate && (
          <button
            onClick={createNew}
            className="w-full mt-1 px-2 py-1.5 text-sm rounded text-[#145c8c] hover:bg-slate-50 flex items-center gap-2"
            data-testid={`${testId}-create`}
          >
            <Plus className="h-3.5 w-3.5" />
            Create &ldquo;{search.trim()}&rdquo;
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
